const hydrateRockMamaMegaImages = (scope) => {
  if (!scope) return;

  for (const template of scope.querySelectorAll('template[data-rm-mega-image]')) {
    template.replaceWith(template.content.cloneNode(true));
  }
};

const rockMamaProductRequests = new Map();

const loadRockMamaProducts = (url) => {
  if (!rockMamaProductRequests.has(url.href)) {
    const requestUrl = new URL(url);
    requestUrl.searchParams.set('section_id', 'rm-menu-products');
    const request = fetch(requestUrl, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Products unavailable');
        return response.text();
      })
      .then((html) => {
        const content = new DOMParser().parseFromString(html, 'text/html')
          .querySelector('[data-rm-mega-response]');
        if (!content) throw new Error('Product markup unavailable');
        return { html: content.innerHTML, title: content.getAttribute('aria-label') };
      })
      .catch((error) => {
        rockMamaProductRequests.delete(url.href);
        throw error;
      });
    rockMamaProductRequests.set(url.href, request);
  }
  return rockMamaProductRequests.get(url.href);
};

const initRockMamaMegaMenus = (scope = document) => {
  for (const menu of scope.querySelectorAll('[data-rm-megamenu]')) {
    if (menu.dataset.rmMegamenuReady === 'true') continue;

    const triggers = [...menu.querySelectorAll('[data-rm-mega-target]')];
    const panels = [...menu.querySelectorAll('[data-rm-mega-panel]')];
    const panelLoaders = new Map();

    for (const panel of panels) {
      const products = panel.querySelector('.rm-megamenu__products');
      const initialUrl = new URL(products.dataset.rmProductsUrl || location.href, location.origin).href;
      let selectedUrl = products.hasAttribute('data-rm-products-rendered') ? initialUrl : '';
      let revision = 0;
      const initialContent = { html: products.innerHTML, title: products.getAttribute('aria-label') };

      // `link` is a sublink, or the products container itself when the active sublink is not a collection.
      const selectProducts = async (link) => {
        const url = new URL(link.dataset.rmProductsUrl || link.href, location.origin);
        if (url.origin !== location.origin || url.href === selectedUrl) return;
        selectedUrl = url.href;
        const currentRevision = ++revision;
        const label = link === products ? products.getAttribute('aria-label') || '' : link.textContent.trim();
        if (link !== products) {
          for (const item of panel.querySelectorAll('.rm-megamenu__sublinks li')) {
            item.classList.toggle('is-active', item.contains(link));
          }
        }

        const status = document.createElement('p');
        status.className = 'rm-megamenu__status';
        status.setAttribute('role', 'status');
        status.textContent = 'Loading products…';
        const shopAll = document.createElement('a');
        shopAll.className = 'rm-megamenu__more';
        shopAll.href = url.href;
        shopAll.textContent = (menu.dataset.rmShopAllLabel || 'Shop all [collection]').replace('[collection]', label);
        products.replaceChildren(status, shopAll);
        products.setAttribute('aria-busy', 'true');
        products.setAttribute('aria-label', label);
        try {
          const content = url.href === initialUrl && products.hasAttribute('data-rm-products-rendered')
            ? initialContent
            : await loadRockMamaProducts(url);
          if (revision !== currentRevision || !products.isConnected) return;
          products.innerHTML = content.html;
          if (content.title) products.setAttribute('aria-label', content.title);
          const collectionLink = products.querySelector('.rm-megamenu__more');
          if (collectionLink && link !== products) collectionLink.textContent = (menu.dataset.rmShopAllLabel || 'Shop all [collection]').replace('[collection]', label);
          hydrateRockMamaMegaImages(products);
          layoutProducts();
        } catch {
          if (revision !== currentRevision) return;
          selectedUrl = '';
          status.textContent = 'Unable to load products. Open this page to view more.';
        } finally {
          if (revision === currentRevision) products.removeAttribute('aria-busy');
        }
      };

      for (const link of panel.querySelectorAll('[data-rm-mega-products]')) {
        link.addEventListener('pointerenter', () => selectProducts(link));
        link.addEventListener('focusin', () => selectProducts(link));
      }

      panelLoaders.set(panel, () => {
        const activeLink = panel.querySelector('.rm-megamenu__sublinks li.is-active [data-rm-mega-products]');
        if (activeLink) selectProducts(activeLink);
        else if (products.dataset.rmProductsUrl && !products.hasAttribute('data-rm-products-rendered')) selectProducts(products);
      });
    }

    const activate = (trigger) => {
      const targetId = trigger.dataset.rmMegaTarget;

      for (const candidate of triggers) {
        const active = candidate === trigger;
        candidate.closest('li')?.classList.toggle('is-active', active);
        candidate.setAttribute('aria-expanded', String(active));
      }

      for (const panel of panels) {
        const active = panel.id === targetId;
        panel.hidden = !active;
        if (active) {
          hydrateRockMamaMegaImages(panel);
          panelLoaders.get(panel)?.();
        }
      }
    };

    for (const trigger of triggers) {
      trigger.addEventListener('pointerenter', () => activate(trigger));
      trigger.addEventListener('focusin', () => activate(trigger));
    }

    const layoutProducts = () => {
      // The menu opens at the bottom edge of its offset parent; CSS sizes the
      // product cards to the viewport height left below that edge.
      const anchor = menu.offsetParent;
      const products = menu.querySelector('[data-rm-mega-panel]:not([hidden]) .rm-megamenu__products');
      if (!anchor || !products) return;

      const top = Math.ceil(anchor.getBoundingClientRect().bottom);
      menu.style.setProperty('--rm-mega-top', `${top}px`);
    };
    const prepareActivePanel = () => {
      const activePanel = menu.querySelector('[data-rm-mega-panel]:not([hidden])');
      panelLoaders.get(activePanel)?.();
      layoutProducts();
      hydrateRockMamaMegaImages(activePanel);
    };
    const parent = menu.closest('.site-nav--is-megamenu');
    parent?.addEventListener('pointerenter', prepareActivePanel);
    parent?.addEventListener('focusin', prepareActivePanel);
    window.addEventListener('resize', layoutProducts);

    menu.dataset.rmMegamenuReady = 'true';
  }
};

const initRockMamaMobileMegaMenus = (scope = document) => {
  for (const menu of scope.querySelectorAll('.rm-mobile-nav__mega-parent')) {
    if (menu.dataset.rmMobileMegamenuReady === 'true') continue;

    const hydrateOpenCategory = () => {
      hydrateRockMamaMegaImages(menu.querySelector('.rm-mobile-mega details[open]'));
    };

    menu.addEventListener('click', (event) => {
      if (!event.target.closest('.collapsible-trigger, summary')) return;
      requestAnimationFrame(hydrateOpenCategory);
    });

    for (const disclosure of menu.querySelectorAll('.rm-mobile-mega details')) {
      disclosure.addEventListener('toggle', () => {
        if (disclosure.open) hydrateRockMamaMegaImages(disclosure);
      });
    }


    menu.dataset.rmMobileMegamenuReady = 'true';
  }
};

const resetRockMamaMobileNavigation = () => {
  const drawer = document.querySelector('#NavDrawer');
  if (!drawer) return;

  for (const trigger of drawer.querySelectorAll('.rm-mobile-nav__root-trigger')) {
    const content = document.getElementById(trigger.getAttribute('aria-controls'));
    trigger.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    content?.classList.remove('is-open');
    if (content) content.style.height = '0px';
  }

  const scrollable = drawer.querySelector('.drawer__scrollable');
  if (scrollable) scrollable.scrollTop = 0;
};

const initRockMamaNavigation = (scope = document) => {
  initRockMamaMegaMenus(scope);
  initRockMamaMobileMegaMenus(scope);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initRockMamaNavigation());
} else {
  initRockMamaNavigation();
}

document.addEventListener('shopify:section:load', (event) => initRockMamaNavigation(event.target));
document.addEventListener('drawerOpen.NavDrawer', () => {
  resetRockMamaMobileNavigation();
  for (const disclosure of document.querySelectorAll('#NavDrawer .rm-mobile-mega details[open]')) {
    hydrateRockMamaMegaImages(disclosure);
  }
});
